// 261010 Epic 2 / P6 — CHÍNH SÁCH phiếu "AI chấm clip" + chọn bản clip (thuần: không antd/router, jest chạy được).
// Phiếu chỉ HIỆN (User 25/10: "Chỉ hiện phiếu, không chặn"): tag trên từng bản · AI đề xuất bản tốt nhất (người bấm chọn, không tự đổi) · chọn bản chưa sạch chỉ hỏi LÝ DO, không chặn cứng.
// Lượt 2: nguồn dữ liệu của dòng bản clip + "AI đề xuất" là `clipBoards[]` THẬT của BE (`_p6/dev-be-1.md` §8) — FE KHÔNG tự xếp hạng, KHÔNG tự đánh số "Bản N" khi BE đã trả `order`.
// `clipBoards` vắng (video chưa có phiếu clip nào / BE cũ) ⇒ mọi bản "chưa chấm", không đề xuất.
import type { FilmRunningJob } from '../../types/film';
import type { ClipBoard, ClipReviewState, ClipSnapshotMeta, StepReview, StepReviewItem, StepReviewVerdict, StepReviewsResponse } from '../../types/filmStepReview';
import type { WorkflowEventStatus } from '../../types/filmWorkflow';
import type { ViewerTarget } from './ReviewMediaViewer';
import { safeMediaUrl } from './reviewMediaPolicy';
import { CL_REVIEW, REVIEW_TITLE, isClipSnapshot, runButton, timeLabelOf, timeOf, type FindingView, type ReviewChip, type RunButton } from './stepReviewPolicy';

export interface ClipLite { id: string; status?: string | null; videoUrl?: string | null }
export interface SelectionFacts { selectedClipId?: string | null; selectionSource?: string | null; selectionClipId?: string | null }
export interface SelectionTag { tone: 'ok' | 'warn' | 'muted'; label: string }
export interface SelectGate {
  required: boolean; reasons: string[];
  /** Phiếu có thể ghi LÝ DO (decide ACCEPT + note): phiếu xong, còn mới, chưa phân xử, kết luận FIX_REQUIRED / NEEDS_LEADER. Không ghi được ⇒ null (lý do chỉ để xác nhận). */
  reviewId: string | null;
}

/** Bản chấm được: clip DONE và CÓ video (cùng điều kiện BE đánh số "Bản N" và nhận lượt chấm). */
const canReview = (c: ClipLite): boolean => c.status === 'DONE' && !!c.videoUrl && !!c.videoUrl.trim();

// ───────────────────────── tra phiếu / bảng bản clip ─────────────────────────

/** Mỗi (video, bản clip) một nhóm phiếu: khớp đủ ba khoá — phiếu của bản khác KHÔNG được rò sang bản này. */
export function clipItemOf(resp: StepReviewsResponse | null | undefined, eventId: string, clipId: string | null | undefined): StepReviewItem | null {
  if (!clipId) return null;
  return resp?.reviews?.find((i) => i.eventId === eventId && i.stepKey === CL_REVIEW && i.candidateClipId === clipId) ?? null;
}
/** Khối "các bản clip" của BE cho video. Vắng ⇒ null (mọi bản coi như chưa chấm). */
export const clipBoardOf = (resp: StepReviewsResponse | null | undefined, eventId: string): ClipBoard | null => resp?.clipBoards?.find((b) => b.eventId === eventId) ?? null;

/**
 * "Bản N" của từng clip. BE trả `order` thì dùng ĐÚNG số đó (BE đánh số theo createdAt trong các bản DONE có video; `order: 0` = không đánh số ⇒ không có số);
 * clip FE biết mà board chưa liệt kê (mới gen) hoặc chưa có board ⇒ số cục bộ theo thứ tự các bản chấm được (cùng cách BE đánh số).
 */
export function clipOrdersOf(clips: ClipLite[], board: ClipBoard | null): Record<string, number> {
  const out: Record<string, number> = {};
  let n = 0;
  for (const c of clips) {
    if (!canReview(c)) continue;
    n += 1;
    const bc = board?.candidates.find((x) => x.clipId === c.id);
    if (bc) { if (bc.order > 0) out[c.id] = bc.order; } else out[c.id] = n;
  }
  return out;
}
export const clipLabelOf = (order: number): string => `Bản ${order}`;
export const clipReviewTitle = (label?: string | null): string => (label && label.trim() ? `${REVIEW_TITLE.CLIP_REVIEW} · ${label.trim()}` : REVIEW_TITLE.CLIP_REVIEW);

// ───────────────────────── bản đang xem phiếu ─────────────────────────

/** Mặc định xem phiếu của bản đang chọn (nếu chấm được), không thì bản chấm được mới nhất; chưa có ⇒ null (không có gì để chấm). */
export function defaultFocusClipId(clips: ClipLite[], selectedClipId?: string | null): string | null {
  const sel = selectedClipId ? clips.find((c) => c.id === selectedClipId && canReview(c)) : undefined;
  if (sel) return sel.id;
  const ok = clips.filter(canReview);
  return ok.length ? ok[ok.length - 1].id : null;
}
/** Người xem chọn bản nào thì giữ; bản đó mất / không còn chấm được ⇒ về mặc định. */
export const effectiveFocusClipId = (clips: ClipLite[], selectedClipId: string | null | undefined, wanted: string | null): string | null =>
  (wanted && clips.some((c) => c.id === wanted && canReview(c)) ? wanted : defaultFocusClipId(clips, selectedClipId));

// ───────────────────────── tag trên dòng bản clip ─────────────────────────

const CLIP_CHIP: Record<StepReviewVerdict, ReviewChip> = {
  PASS: { tone: 'ok', label: 'Đạt' },
  FIX_REQUIRED: { tone: 'bad', label: 'Cần sửa' },
  NEEDS_LEADER: { tone: 'warn', label: 'Cần bạn xem' },
  INCOMPLETE: { tone: 'muted', label: 'Chấm dở' },
};
/** Tag review của MỘT bản clip theo trạng thái phiếu BE trả (`NONE` = clip cũ / chưa chấm). Mã kết luận gốc nằm ở `title`. */
export function clipReviewChip(review: ClipReviewState): ReviewChip & { title: string } {
  if (review.state === 'NONE') return { tone: 'muted', label: 'chưa chấm', title: 'Chưa chấm' };
  if (review.state === 'RUNNING') return { tone: 'muted', label: 'Đang chấm…', title: 'RUNNING' };
  const base = (review.verdict && CLIP_CHIP[review.verdict]) || CLIP_CHIP.INCOMPLETE;
  return { tone: base.tone, label: review.state === 'STALE' ? `${base.label} · phiếu cũ` : base.label, title: review.verdict ?? 'INCOMPLETE' };
}

/** Dữ kiện "ai đã chọn bản nào": có board ⇒ của board (nguồn BE cùng lượt đọc với phiếu); không ⇒ của event (GET events). */
export function selectionFactsOf(board: ClipBoard | null, ev: SelectionFacts): SelectionFacts {
  if (board) return { selectedClipId: board.selectedClipId, selectionSource: board.selectionSource };
  return { selectedClipId: ev.selectedClipId ?? null, selectionSource: ev.selectionSource ?? null, selectionClipId: ev.selectionClipId ?? null };
}
/**
 * "Chọn tạm (tự động)" (`UI_AUTO` — lúc gen xong) vs "Người đã chọn" (`HUMAN`) vs "AI ngoài đã chọn" (`AGENT`); chỉ gắn cho bản ĐANG chọn.
 * Nguồn vắng / lạ ⇒ không gắn nhãn (không đoán). Dấu ghi một clip khác với clip đang chọn là dấu cũ ⇒ bỏ.
 */
export function selectionTagOf(ev: SelectionFacts, clipId: string): SelectionTag | null {
  if (!ev.selectedClipId || ev.selectedClipId !== clipId) return null;
  if (ev.selectionClipId && ev.selectionClipId !== ev.selectedClipId) return null;
  if (ev.selectionSource === 'HUMAN') return { tone: 'ok', label: 'Người đã chọn' };
  if (ev.selectionSource === 'UI_AUTO') return { tone: 'warn', label: 'Chọn tạm (tự động)' };
  if (ev.selectionSource === 'AGENT') return { tone: 'muted', label: 'AI ngoài đã chọn' };
  return null;
}

// ───────────────────────── view-model danh sách bản clip ─────────────────────────

export interface ClipRowView {
  clipId: string; order: number; label: string;
  /** Trạng thái phiếu của BE cho bản này (`NONE` khi chưa chấm / không có board). */
  review: ClipReviewState;
  item: StepReviewItem | null;
  /** false ⇒ BE nói engine / số giây lệch video: `PUT select-clip` sẽ bị từ chối. null = không có board (BE chưa nói). */
  usable: boolean | null;
  recommended: boolean; selection: SelectionTag | null; focused: boolean; run: RunButton;
  /** Đang gửi lệnh chấm bản này (POST chậm vài chục giây) ⇒ spinner. */
  sending: boolean;
  /** Số lỗi nặng hợp lệ của phiếu CÒN MỚI (V5 và finding unverified BE đã loại). */
  blockingCount: number;
  /** "Đạt 3/4 nhóm · điểm 85" (phiếu còn mới). */
  detailLine: string | null;
  /** Vì sao bản này đứng hạng đó / không được đề xuất — chữ của BE. */
  rankNote: string | null;
}
export interface ClipRecommendationView { clipId: string | null; line: string | null }

/**
 * Mọi thứ danh sách bản clip cần để vẽ dòng review (bước Video import antd nên không test render được ⇒ nối dây nằm ở đây, có test).
 * Chỉ bản chấm được (DONE + video + có số "Bản N") có dòng. Đề xuất = `clipBoards[].recommendation` của BE nguyên văn (không tự xếp hạng).
 * Nút chấm từng bản: khoá khi đang gửi lệnh (một việc một lúc) · máy tự chạy chặng · phiếu / job của CHÍNH bản đó đang chạy (job của bản khác không khoá).
 */
export function clipRowsView(o: {
  clips: ClipLite[]; ev: SelectionFacts; resp: StepReviewsResponse | null | undefined; eventId: string;
  jobs?: Array<Pick<FilmRunningJob, 'type' | 'key' | 'eventId'>>; busy: boolean; busyClipId: string | null; runActive: boolean; focusClipId: string | null;
}): { rows: Record<string, ClipRowView>; recommendation: ClipRecommendationView } {
  const board = clipBoardOf(o.resp, o.eventId);
  const orders = clipOrdersOf(o.clips, board);
  const facts = selectionFactsOf(board, o.ev);
  const recId = board?.recommendation.recommendedClipId ?? null;
  const rows: Record<string, ClipRowView> = {};
  for (const c of o.clips) {
    const order = orders[c.id];
    if (!order) continue;
    const bc = board?.candidates.find((x) => x.clipId === c.id);
    const review: ClipReviewState = bc?.review ?? { state: 'NONE' };
    const item = clipItemOf(o.resp, o.eventId, c.id);
    const fresh = review.state === 'DONE' ? review : null;
    const parts = fresh ? [fresh.groupsOk != null ? `Đạt ${fresh.groupsOk}/4 nhóm` : '', fresh.score != null ? `điểm ${fresh.score}` : ''].filter(Boolean) : [];
    const rank = board?.recommendation.ranking.find((x) => x.clipId === c.id);
    const rankNote = !rank ? null : rank.eligible && rank.rank != null ? `Xếp hạng #${rank.rank}: ${rank.reason}` : !rank.eligible && review.state !== 'NONE' ? `Chưa đề xuất: ${rank.reason}` : null;
    rows[c.id] = {
      clipId: c.id, order, label: clipLabelOf(order), review, item, usable: bc ? bc.usable : null,
      recommended: recId === c.id, selection: selectionTagOf(facts, c.id), focused: o.focusClipId === c.id,
      run: runButton(item, { busy: o.busy, runActive: o.runActive, jobs: o.jobs, eventId: o.eventId, stepKey: CL_REVIEW, candidateClipId: c.id }),
      sending: o.busy && o.busyClipId === c.id,
      blockingCount: fresh ? fresh.blocking.length : 0, detailLine: parts.length ? parts.join(' · ') : null, rankNote,
    };
  }
  const recRow = recId ? rows[recId] : undefined;
  const summary = board?.recommendation.summary?.trim() ?? '';
  return { rows, recommendation: recId ? { clipId: recId, line: summary || (recRow ? `AI đề xuất ${recRow.label}.` : null) } : { clipId: null, line: null } };
}

// ───────────────────────── chọn bản: hỏi lý do, KHÔNG chặn cứng ─────────────────────────

/**
 * Chọn bản có lỗi nặng / NEEDS_LEADER chưa phân xử, hoặc chưa chấm ⇒ cần ghi LÝ DO (Leader Rev 1). Chỉ hỏi khi video ĐÃ có clipBoard (≥ 1 phiếu clip):
 * video chưa có phiếu nào (luồng cũ, clip cũ không backfill) thì chọn bản y như trước, không thêm hộp nào (0-diff). Đã được Leader phân xử ⇒ lý do đã nằm trong phiếu, không hỏi lại.
 * Lý do được LƯU bằng `decide ACCEPT + note` lên phiếu của bản đó (BE không nhận `note` ở select-clip) — chỉ làm được khi `reviewId` khác null.
 */
export function selectGate(o: { resp: StepReviewsResponse | null | undefined; eventId: string; targetClipId: string }): SelectGate {
  const board = clipBoardOf(o.resp, o.eventId);
  if (!board) return { required: false, reasons: [], reviewId: null };
  const rv = board.candidates.find((c) => c.clipId === o.targetClipId)?.review ?? { state: 'NONE' as const };
  const ask = (reason: string, reviewId: string | null = null): SelectGate => ({ required: true, reasons: [reason], reviewId });
  if (rv.state === 'NONE') return ask('Bản này chưa được AI chấm.');
  if (rv.state === 'RUNNING') return ask('Bản này đang được chấm, chưa có kết luận.');
  if (rv.state === 'STALE') return ask('Phiếu của bản này đã cũ (dữ liệu đã đổi sau lúc chấm).');
  if (!rv.verdict || rv.verdict === 'INCOMPLETE') return ask('Bản này chưa có phiếu hoàn chỉnh (chấm dở).');
  if (rv.decided) return { required: false, reasons: [], reviewId: null };
  const n = rv.blocking.length;
  if (n > 0) return ask(`Phiếu báo ${n} lỗi nặng chưa được phân xử.`, rv.reviewId);
  if (rv.verdict === 'NEEDS_LEADER') return ask('Hai giám khảo chưa thống nhất, phiếu chưa được phân xử.', rv.reviewId);
  if (rv.verdict === 'FIX_REQUIRED') return ask('Phiếu báo lỗi nặng chưa được phân xử.', rv.reviewId);
  return { required: false, reasons: [], reviewId: null };
}
export const validateSelectReason = (note: string): string | null => (note.trim() ? null : 'Ghi lý do chọn bản này (bản có lỗi nặng hoặc chưa chấm) trước khi xác nhận.');

// ───────────────────────── bấm finding ⇒ khung hình / âm thanh ─────────────────────────

export const isClipSnapshotOf = (r: StepReview | null | undefined): ClipSnapshotMeta | null => (r && isClipSnapshot(r.snapshot) ? r.snapshot : null);
const NEAR_EPS_S = 0.05;
const kindLabel = (f: { kind?: string; pct?: number }): string => (f.kind === 'CUT' ? 'mốc cắt nhịp' : typeof f.pct === 'number' ? `mốc ${f.pct}%` : '');
/**
 * Khung hình BE đã trích (`snapshot.frames[]`) cho finding (BE §8): `frameNo` thuộc bộ khung ⇒ khung đó (sai số khung ⇒ null, KHÔNG tụt về giây);
 * không có `frameNo` ⇒ khung GẦN NHẤT theo `timestampS` (ghi rõ "gần nhất" khi không trùng giây). Khung đó không có ảnh (`storedUrl` null / URL không an toàn) ⇒ null: UI chỉ in giây, không nhảy sang khung khác.
 */
export function frameTargetOf(f: Pick<FindingView, 'frameNo' | 'timestampS' | 'evidence'>, r: StepReview | null | undefined, title: string): ViewerTarget | null {
  const snap = isClipSnapshotOf(r);
  if (!snap) return null;
  const frames = snap.frames ?? [];
  let frame: (typeof frames)[number] | undefined;
  let near = false;
  if (f.frameNo >= 1) frame = frames.find((x) => x.frameNo === f.frameNo);
  else if (f.timestampS !== null) {
    const at = f.timestampS;
    let best = Number.POSITIVE_INFINITY;
    for (const x of frames) {
      const t = timeOf(x.timestampS);
      if (t === null) continue;
      const d = Math.abs(t - at);
      if (d < best) { best = d; frame = x; }
    }
    near = best > NEAR_EPS_S;
  }
  const src = frame ? safeMediaUrl(frame.storedUrl) : null;
  if (!frame || !src) return null;
  const k = kindLabel(frame);
  return {
    src, title: `${title} — khung hình đã gửi cho giám khảo`,
    lines: [
      `Khung ${frame.frameNo}${k ? ` · ${k}` : ''} · ${timeLabelOf(timeOf(frame.timestampS))}`,
      near && f.timestampS !== null ? `Khung hình gần nhất cho mốc ${timeLabelOf(f.timestampS)}: khung ${frame.frameNo} (${timeLabelOf(timeOf(frame.timestampS))}).` : '',
      f.evidence,
    ].filter(Boolean),
    region: null,
  };
}
/** File âm thanh giám khảo nghe đã nghe: `url` null ⇒ BE không lưu được file (hoặc URL không an toàn) ⇒ UI nói rõ, KHÔNG có nút chết. `NO_TRACK` ⇒ clip không có tiếng. */
export function clipAudioOf(r: StepReview | null | undefined): { status: 'JUDGED' | 'NO_TRACK' | null; url: string | null } {
  const snap = isClipSnapshotOf(r);
  if (!snap) return { status: null, url: null };
  return { status: snap.audioStatus === 'NO_TRACK' ? 'NO_TRACK' : snap.audioStatus === 'JUDGED' ? 'JUDGED' : null, url: safeMediaUrl(snap.audio?.storedUrl) };
}

// ───────────────────────── cảnh báo ở bước Ghép (chỉ chữ, KHÔNG chặn) ─────────────────────────

export interface AssemblyClipWarning { eventId: string; eventOrder: number; kind: 'UNRATED' | 'STALE' | 'RUNNING' | 'INCOMPLETE' | 'UNDECIDED'; text: string }
/**
 * Với mỗi video ĐÃ chọn clip, nhìn khoá `workflow-status.events[].reviews.CLIP_REVIEW` (BE chỉ ghi cho clip đang chọn, và KHÔNG sinh alert "chưa chấm" để plan cũ khỏi đầy cảnh báo):
 * vắng ⇒ "chưa chấm" · STALE ⇒ "phiếu cũ" · FIX_REQUIRED / NEEDS_LEADER chưa phân xử ⇒ "chưa phân xử" · RUNNING / INCOMPLETE ⇒ nói tình trạng · PASS hay đã phân xử ⇒ im.
 * Khoá mang `candidateClipId` khác clip đang chọn trong chi tiết plan ⇒ coi như vắng (chưa chấm). `detailEvents` null (chưa nạp chi tiết plan) ⇒ chưa biết video nào đã chọn clip ⇒ không cảnh báo. Không video nào bị chặn ghép vì các dòng này.
 */
export function assemblyClipWarnings(events: Array<Pick<WorkflowEventStatus, 'eventId' | 'eventOrder' | 'reviews'>>, detailEvents: Array<{ id: string; selectedClipId?: string | null }> | null | undefined): AssemblyClipWarning[] {
  if (!detailEvents) return [];
  const selected = new Map(detailEvents.filter((e) => !!e.selectedClipId).map((e) => [e.id, e.selectedClipId as string] as const));
  const out: AssemblyClipWarning[] = [];
  for (const e of [...events].sort((a, b) => a.eventOrder - b.eventOrder)) {
    const clipId = selected.get(e.eventId);
    if (!clipId) continue;
    // Hai nguồn (chi tiết plan / workflow-status) nạp vào những lúc khác nhau: khoá trỏ MỘT BẢN KHÁC bản đang chọn ⇒ kết luận đó không phải của bản này ⇒ coi như chưa chấm (không mượn verdict của bản cũ).
    const raw = e.reviews?.CLIP_REVIEW;
    const b = raw && raw.candidateClipId && raw.candidateClipId !== clipId ? undefined : raw;
    const w = (kind: AssemblyClipWarning['kind'], text: string): void => { out.push({ eventId: e.eventId, eventOrder: e.eventOrder, kind, text: `Video ${e.eventOrder}: ${text}` }); };
    if (!b) w('UNRATED', 'bản clip đang chọn chưa được AI chấm.');
    else if (b.verdict === 'STALE') w('STALE', 'phiếu AI chấm của bản clip đang chọn đã cũ.');
    else if (b.verdict === 'RUNNING') w('RUNNING', 'AI đang chấm bản clip đang chọn.');
    else if (b.verdict === 'INCOMPLETE') w('INCOMPLETE', 'phiếu AI chấm của bản clip đang chọn chấm dở, chưa có kết luận.');
    else if ((b.verdict === 'FIX_REQUIRED' || b.verdict === 'NEEDS_LEADER') && !b.decided) w('UNDECIDED', 'phiếu AI chấm báo lỗi, chưa phân xử.');
  }
  return out;
}
